export enum SocketState {
  NO_SOCKET = -1,
  CONNECTING = 0,
  OPEN = 1,
  CLOSING = 2,
  CLOSED = 3,
}

interface IWebsocketState {
  url?: string;
  readyState?: number;
  checkedForConnectionError?: boolean;
  permissionsErrorNoReconnect?: boolean;
}

export type IWebsocketHandler = (event: MessageEvent) => void;

export type IWebsocketStateChangeCallback = (
  inst: ReconnectingWebsocket,
  connected: boolean
) => void;

export default class ReconnectingWebsocket {
  public readyState: number = SocketState.NO_SOCKET;

  public permissionsErrorNoReconnect: boolean = false;
  public reconnectStartTime?: number;

  private debug: boolean = true;
  private readonly propsUrl: string;
  private url: string;
  private checkedForConnectionError: boolean = false;
  private shouldReconnect?: boolean;
  private socket?: WebSocket;
  private reconnectTimeout?: number;

  private firstConnectAttempt: boolean = false;

  constructor(
    url: string,
    private readonly onMessage: IWebsocketHandler = () => {
      /**/
    },
    private readonly onStateChanged: IWebsocketStateChangeCallback = () => {
      /**/
    },
    debug: boolean = false,
    private binaryType?: BinaryType,
    private onError?: (error: Error) => void
  ) {
    this.reconnectStartTime = Date.now();
    this.propsUrl = url;
    this.setUrl(url);
    this.url = url; // we must set the url in the constructor
    this.debug = debug;
  }

  get connected(): boolean {
    return this.readyState === SocketState.OPEN;
  }

  public send(msg: string | ArrayBufferLike): void {
    if (this.socket !== undefined && this.socket.readyState === SocketState.OPEN) {
      this.socket.send(msg);
    } else {
      this.debugLog("Unable to send message while websocket was closed");
    }
  }

  public status(): string {
    switch (this.readyState) {
      case SocketState.NO_SOCKET:
        return "NO_SOCKET";
      case SocketState.CONNECTING:
        return "CONNECTING";
      case SocketState.OPEN:
        return "OPEN";
      case SocketState.CLOSING:
        return "CLOSING";
      case SocketState.CLOSED:
        return "CLOSED";
      default:
        return "UNKNOWN_" + this.readyState;
    }
  }

  public destroy() {
    this.shouldReconnect = false;
    this.setUrl();
    // NOTE(jeff): setUrl() triggers an autoConnect() cycle, but we're unmounting so we don't
    //  actually want that, so we clear the timeout that just got created.
    clearTimeout(this.reconnectTimeout);
  }

  public reconnect() {
    if (this.readyState !== SocketState.OPEN && !this.permissionsErrorNoReconnect) {
      this.shouldReconnect = true;
      this.debugLog("Setting should reconnect");
      this.setUrl();
    }
  }

  protected debugLog(...args: any[]) {
    if (this.debug) {
      console.log.apply(console, args);
    }
  }

  private broadcastState(): void {
    this.onStateChanged(this, this.connected);
    if (this.shouldReconnect) {
      // do this async otherwise we end up in a stack loop
      window?.setTimeout(() => this.setUrl(this.propsUrl), 0);
      this.shouldReconnect = false;
    }
  }

  private setStateAndHandleAutoConnect(newState: IWebsocketState) {
    // If we're setting a new readyState, then we may need to start/stop the autoReconnect timeout
    if (newState.readyState !== undefined) {
      if (newState.readyState === SocketState.OPEN) {
        // If socket now open, make sure we clear the timeout
        clearTimeout(this.reconnectTimeout);
      } else if (this.readyState === SocketState.OPEN || this.firstConnectAttempt) {
        // If socket is was open and now isn't, or if our very first connection attempt failed,
        // update time and start autoReconnect
        this.reconnectStartTime = Date.now();
        this.autoReconnect(true);
      }
    }
    if (this.firstConnectAttempt) {
      this.firstConnectAttempt = false;
    }
    Object.keys(newState).forEach(key => {
      (this as any)[key] = (newState as any)[key];
    });

    this.broadcastState();
  }

  private refreshSocketReadyState() {
    const readyState = this.socket ? this.socket.readyState : SocketState.NO_SOCKET;
    if (this.readyState === readyState) {
      return;
    }
    this.setStateAndHandleAutoConnect({
      readyState,
    });
  }

  private onSocketOpen(url: string) {
    this.debugLog(`Socket opened for url: ${url}`);
    this.refreshSocketReadyState();
  }

  private onSocketClose(url: string, closeEvent: CloseEvent) {
    this.debugLog(`Socket closed for url: ${url}`, closeEvent);
    this.refreshSocketReadyState();
  }

  private onSocketMessage(url: string, event: MessageEvent) {
    this.debugLog(`Socket message for url: ${url}`, event.data);
    // if we are not using a binary transport method and we havent checked yet
    if (this.binaryType === undefined && !this.checkedForConnectionError) {
      try {
        // Check the first message of any connection for a permission error, and reload if found
        // NOTE(jeff): Right now websockets only check permissions on connect
        if (event.data.indexOf('"PermissionsError"') !== -1) {
          const msg = JSON.parse(event.data);
          if (msg.error && msg.error.type === "PermissionsError") {
            console.log("Received permissions error for websocket! Not reconnecting");
            this.setStateAndHandleAutoConnect({
              permissionsErrorNoReconnect: true,
            });
            return;
          }
        }
      } catch (ex) {
        console.log(ex);
      }
      this.checkedForConnectionError = true;
    }
    this.onMessage(event);
    this.refreshSocketReadyState();
  }

  private onSocketError(url: string, errorEvent: Event) {
    this.debugLog(`Socket error for url: ${url}`, errorEvent);
    this.refreshSocketReadyState();
    if (this.onError) {
      // there's a disappointing amount of information available
      // on error to prevent network probing.
      this.onError(new Error("Socket error"));
    }
  }

  private autoReconnect(firstCall?: boolean) {
    if (this.permissionsErrorNoReconnect) {
      return;
    }

    // Set the next autoReconnect timeout to backoff exponentially
    const timeSinceReconnectStart = Math.abs(Date.now() - (this.reconnectStartTime || 0));
    // Don't reconnect for at least 1.5s
    const timeoutDelay = Math.max(1500, timeSinceReconnectStart);
    window?.clearTimeout(this.reconnectTimeout);
    this.reconnectTimeout = window?.setTimeout(this.autoReconnect.bind(this), timeoutDelay);
    if (!firstCall) {
      this.debugLog("autoReconnect scheduled again in " + timeoutDelay + "ms for " + this.url);
    }
    // prevent overloading server when connections don't immediately connect
    if (this.socket && this.socket.readyState === SocketState.CONNECTING) {
      this.debugLog("Holding off on reconnecting, still connecting");
      return;
    }
    // NOTE(jeff): do the reconnect after setting the timeout, so that if it
    // works it clears the timeout
    this.reconnect();
  }

  private setUrl(url: string = "") {
    if (this.url === url) {
      return;
    }
    this.url = url;
    if (
      this.socket &&
      (this.readyState === SocketState.OPEN || this.readyState === SocketState.CONNECTING)
    ) {
      this.debugLog("Closing socket for url: " + this.url);
      this.socket.close();
      this.socket.onopen = null;
      this.socket.onclose = null;
      this.socket.onmessage = null;
      this.socket.onerror = null;
      this.socket = undefined;
    }
    this.setStateAndHandleAutoConnect({
      url: "",
      readyState: SocketState.NO_SOCKET,
      checkedForConnectionError: false,
    });
    if (!url) {
      return;
    }
    this.debugLog(`Connecting to url: ${url}`);
    this.socket = new WebSocket(url);
    if (this.binaryType) {
      this.socket.binaryType = this.binaryType;
    }
    this.socket.onopen = this.onSocketOpen.bind(this, url);
    this.socket.onclose = this.onSocketClose.bind(this, url);
    this.socket.onmessage = this.onSocketMessage.bind(this, url);
    this.socket.onerror = this.onSocketError.bind(this, url);

    this.firstConnectAttempt = true;

    this.setStateAndHandleAutoConnect({
      url,
      readyState: this.socket.readyState,
      checkedForConnectionError: false,
    });
  }
}
